import { ComponentInternalInstance, getCurrentInstance, reactive, nextTick, ref } from '@vue/composition-api';
import QRCode from 'qrcode';
import { PollUntil } from 'poll-until-promise';
import i18n, { i18n_t } from '@/locales/i18n';
import { getSpaceAndUserInfo } from '@/renderer/scripts/utils/user/getUserSpaceInfo';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import wsManager from '@/renderer/scripts/utils/wsManager';
import { transformErrorForSerialization } from '@/common/tools';
export enum QRCodeStatus {
  NEW = 'new',
  SCANNED = 'scanned',
  NEEDTOCONFIRM = 'needToConfirm',
  CONFIRMED = 'confirmed',
  CODERETURNED = 'codeReturned',
  FINISHED = 'finished',
  EXPIRED = 'expired',
  ERROR = 'error',
}
export default function qrcodeHandler() {
  const vm = (getCurrentInstance() as ComponentInternalInstance)?.proxy;
  const loading = ref(false);
  const qrcodeStatus = ref<QRCodeStatus>(QRCodeStatus.NEW);
  const keyData = reactive({
    key: '',
    avatar: '',
    nickname: '',
    renderExpired: false, // 前端二维码扫描动作未进行前的超时
  });

  /**
   * 二维码未扫描的超时时间为 10 分钟
   * 后台根据 key 未取到 code 超时时间为 120 分钟
   * 进入授权状态后未点击授权的超时时间为 10 分钟
   * 因此将轮询的总超时时间设定为 150 分钟
   */
  const expiredTime = 2.5 * 3600;
  const isStartPolling = ref(false); // 获取电子签状态是否降级为轮询
  let pollUntil: PollUntil | null = null;
  const methods = {
    async getQRCodeUrl(wpsResult: any) {
      loading.value = true;
      const { path, name } = vm.$route.query;
      const { libraryId, spaceId: personalSpaceId } = await getSpaceAndUserInfo();
      const spaceId = (vm.$route.query?.spaceId as string) || personalSpaceId;
      const params = {
        distDirectoryPath: `${path}`,
        distSpaceId: spaceId as string,
        distLibraryId: libraryId as string,
        // originalPath需要拼接文件名
        originalPath: `${path}/${name}`,
        originalSpaceId: spaceId as string,
        originalLibraryId: libraryId as string,
        ...(Boolean(wpsResult) && {
          originalDocSaveInfo: wpsResult,
        }),
      };
      if (wsManager.ws && wsManager.ws.readyState === wsManager.ws.OPEN) {
        wsManager.send({
          type: 'requestEsignKey',
          body: params,
        });
      } else {
        try {
          const { data } = await smh.userAPI.getElectronSignKey(params);
          keyData.key = data.key;
          methods.drawQRCode(keyData.key, 'qrCode');
          // 若 ws 未开启则后续所有 key 相关都只能用轮询
          methods.startPolling();
        } catch (err) {
          // 二维码key获取失败则让用户手动刷新
          qrcodeStatus.value = QRCodeStatus.ERROR;
          methods.stopPolling();
          tips.error(err as MsgError);
        }
        loading.value = false;
      }
    },
    // 绘制二维码
    drawQRCode(key: string, elementId: string) {
      const canvas = document.getElementById(elementId) as HTMLCanvasElement;
      if (!canvas) return;
      const url = key
        ? 'https://res.ess.tencent.cn/qrcode' +
          '?to=TENCENT_CLOUD_DISK_FILE' +
          '&channel=tencent_cloud_disk' +
          `&Key=${keyData.key}`
        : 'https://res.ess.tencent.cn/qrcode';
      QRCode.toCanvas(
        canvas,
        url,
        {
          width: Math.min(canvas?.clientWidth || 170, 170),
          margin: 0,
        },
        (error: any) => {
          if (error) {
            logger.ui.error(i18n_t('绘制二维码失败'), transformErrorForSerialization(error));
          } else {
            methods.resetESignData();
            methods.startWSListener();
          }
        },
      );
    },
    async handleQRCodeStatus(data: SMH.GetElectronSignStatusData) {
      // 忽略与当前二维码对应 key 不一致的状态消息
      if (data.key && data.key !== keyData.key) return;
      const status = data.status as QRCodeStatus;
      if (data.expired) {
        qrcodeStatus.value = QRCodeStatus.EXPIRED;
        methods.stopPolling();
        return;
      }

      // ["new" | "scanned" | "needToConfirm" | "confirmed"|"codeReturned"|"finished"]
      qrcodeStatus.value = status as QRCodeStatus;
      switch (status) {
        case QRCodeStatus.NEEDTOCONFIRM: {
          keyData.nickname = data.nickname;
          keyData.avatar = data.avatar;
          break;
        }
        default: {
          break;
        }
      }
    },
    // websocket 消息监听处理
    wsMessageHandler(e: MessageEvent) {
      // 降级为轮询后 ws 发送的信息不再有效
      if (isStartPolling.value) return;
      try {
        const { type, body: data } = JSON.parse(e.data);
        switch (type) {
          case 'esignKeyStatus': {
            methods.handleQRCodeStatus(data);
            break;
          }
          case 'sendEsignKey': {
            keyData.key = data.key;
            methods.drawQRCode(keyData.key, 'qrCode');
            break;
          }
          case 'err': {
            const error = data;
            tips.error(error);
            if (error.code === 'ESignKeyExpired') {
              qrcodeStatus.value = QRCodeStatus.EXPIRED;
            } else {
              qrcodeStatus.value = QRCodeStatus.ERROR;
            }
            methods.stopPolling();
            break;
          }
          default: {
            break;
          }
        }
      } catch (error) {
        qrcodeStatus.value = QRCodeStatus.ERROR;
        methods.stopPolling();
      }
    },
    // websocket 失败/关闭监听处理
    wsErrorOrCloseHandler() {
      // 只要出错就立即进行轮询，并且将 ws 后续的消息锁住不再处理
      methods.startPolling();
    },
    // 监听当前电子签的授权进度
    startWSListener() {
      wsManager.addEventListener('message', methods.wsMessageHandler);
      wsManager.addEventListener('error', methods.wsErrorOrCloseHandler);
      wsManager.addEventListener('close', methods.wsErrorOrCloseHandler);
    },
    stopWSListener() {
      wsManager.removeEventListener('message', methods.wsMessageHandler);
      wsManager.removeEventListener('error', methods.wsErrorOrCloseHandler);
      wsManager.removeEventListener('close', methods.wsErrorOrCloseHandler);
    },
    startPolling() {
      if (isStartPolling.value) return;
      isStartPolling.value = true;
      pollUntil = new PollUntil();
      pollUntil
        .stopAfter(expiredTime)
        .tryEvery(1000)
        .execute(
          () =>
            new Promise((resolve, reject) => {
              // 如果轮询锁为 false，则轮询应该被返回（即停止轮询）
              if (!isStartPolling.value) {
                resolve(true);
                return;
              }
              smh.userAPI
                .getElectronSignStatus({
                  key: keyData.key,
                })
                .then(({ data }) => {
                  methods.handleQRCodeStatus(data);
                  const needRefresh = [QRCodeStatus.EXPIRED, QRCodeStatus.ERROR].includes(qrcodeStatus.value);
                  // eslint-disable-next-line max-len
                  const isSuccess = [QRCodeStatus.CONFIRMED, QRCodeStatus.CODERETURNED, QRCodeStatus.FINISHED].includes(
                    qrcodeStatus.value,
                  );

                  // 当key的状态变为需要刷新或已完成，轮询被返回（即停止轮询）
                  resolve(needRefresh || isSuccess);
                })
                .catch(err => {
                  // 请求出错则重试
                  resolve(false);
                });
            }),
        )
        .catch((err: Error) => {
          logger.ui(`[esign qrcode polling] ${JSON.stringify(transformErrorForSerialization(err))}`);
          if (err.message.includes('Failed to wait')) {
            // 表示 key 超时，需要刷新
            qrcodeStatus.value = QRCodeStatus.EXPIRED;
          } else {
            qrcodeStatus.value = QRCodeStatus.ERROR;
          }
          methods.stopPolling();
        });
    },
    stopPolling() {
      isStartPolling.value = false;
      pollUntil = null;
    },
    async confirmEsignAuth() {
      const params = {
        key: keyData.key,
      };
      if (!isStartPolling.value && wsManager.ws && wsManager.ws.readyState === wsManager.ws.OPEN) {
        wsManager.send({
          type: 'requestEsignManualConfirm',
          body: params,
        });
      } else {
        try {
          await smh.userAPI.confirmElectronSignKey(params);
          methods.startPolling();
        } catch (error: MsgError) {
          if (error.code === 'ESignKeyExpired') {
            qrcodeStatus.value = QRCodeStatus.EXPIRED;
            methods.stopPolling();
          }
          tips.error(error);
        }
      }
    },
    // 重置电子签授权进度处理
    resetESignData(options?: { resetKey?: boolean }) {
      qrcodeStatus.value = QRCodeStatus.NEW;
      if (options?.resetKey) keyData.key = '';
      keyData.renderExpired = false;
      keyData.nickname = '';
      keyData.avatar = '';
      methods.stopWSListener();
      methods.stopPolling();
    },
  };
  return {
    loading,
    qrcodeStatus,
    keyData,
    methods,
  };
}
